import { useRef, useState } from 'react'
import type React from 'react'
import { Album } from '../../../../types'
import { usePresignedUpload, UploadInProgressError, type UploadProgress } from '../lib/hooks'
import { memoirUploadRefusal } from '../lib/memoir-errors'
import { filterImageFiles } from '../lib/image-files'
import { folderLogger } from '../lib/logger'
import { STORAGE_KEY } from './use-album-collection'
import type { Feedback } from './use-feedback'

export interface AlbumUpload {
  fileInputRef: React.RefObject<HTMLInputElement>
  isDropzoneActive: boolean
  uploadProgress: UploadProgress
  handleUploadToAlbum: (albumId: string) => void
  handleAddPhotosToCurrentAlbum: () => void
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>
  handleDropzoneDragOver: (e: React.DragEvent) => void
  handleDropzoneDragLeave: (e: React.DragEvent) => void
  handleDropzoneDrop: (e: React.DragEvent) => Promise<void>
  handleDropzoneClick: () => void
}

// The hidden file input, the dropzone, and uploadFilesToAlbum -- including
// the two error branches (SOL-150 Task 7): UploadInProgressError, whose
// modal must NOT resetProgress (that would wipe a still-running upload's
// visible progress and previews), and the Memoir refusals (SOL-204), each
// of which gets its own explanation rather than a generic failure.
// usePresignedUpload()
// lives inside this hook now; `uploadProgress` is re-exported because the
// caller's JSX reads it in several places.
export function useAlbumUpload(args: {
  albums: Album[]
  setAlbums: React.Dispatch<React.SetStateAction<Album[]>>
  currentFolderId: string | undefined
  setCurrentFolderId: (id: string | undefined) => void
  fetchAlbums: (silent?: boolean) => Promise<void>
  refreshAlbumFiles: () => void
  feedback: Feedback
}): AlbumUpload {
  const { albums, setAlbums, currentFolderId, setCurrentFolderId, fetchAlbums, refreshAlbumFiles, feedback } = args
  const { showNotification, showToast } = feedback

  const { progress: uploadProgress, uploadFiles, resetProgress } = usePresignedUpload()

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDropzoneActive, setIsDropzoneActive] = useState(false)

  const handleUploadToAlbum = (albumId: string) => {
    folderLogger.info('Upload to album requested', { albumId })
    // Trigger file input click
    if (fileInputRef.current) {
      fileInputRef.current.dataset.albumId = albumId
      fileInputRef.current.click()
    }
  }

  const handleAddPhotosToCurrentAlbum = () => {
    if (currentFolderId && fileInputRef.current) {
      fileInputRef.current.dataset.albumId = currentFolderId
      fileInputRef.current.click()
    }
  }

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    const albumId = e.target.dataset.albumId

    if (files.length === 0 || !albumId) return

    // Reset file input
    e.target.value = ''

    await uploadFilesToAlbum(albumId, files)
  }

  const uploadFilesToAlbum = async (albumId: string, files: File[]) => {
    // Accept all image files including RAW formats (RAW files may not have
    // an image/* MIME type, so extension is checked too - see filterImageFiles)
    const imageFiles = filterImageFiles(files)

    if (imageFiles.length === 0) {
      showNotification('error', 'No Photos Found', 'Please select photo files to add to this album.')
      return
    }

    const album = albums.find(a => a.uuid === albumId)
    const albumName = album?.name || 'Album'
    const isTempAlbum = albumId.startsWith('temp_')

    // A temp album is created on the server by this upload, so it must name
    // its Memoir (SOL-204). One saved in localStorage before Memoirs existed
    // has none, and the server would refuse it; say why here instead.
    if (isTempAlbum && !album?.memoirId) {
      showNotification(
        'error',
        'This album has no Memoir',
        'Albums now live inside a Memoir. Open one of your Memoirs and create the album there.',
      )
      return
    }

    try {
      folderLogger.info('Starting presigned upload', {
        albumId,
        albumName,
        fileCount: imageFiles.length,
        isTempAlbum
      })

      // Use presigned upload for both new and existing albums
      const result = await uploadFiles(
        albumName,
        imageFiles,
        isTempAlbum ? { memoirId: album!.memoirId } : { albumId }
      )

      // Remove temp album from local state if it was a new album
      if (isTempAlbum) {
        const updatedAlbums = albums.filter(a => a.uuid !== albumId)
        setAlbums(updatedAlbums)
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedAlbums))
        // Navigate to the newly created real album
        setCurrentFolderId(result.album.uuid)
      }

      const photoWord = result.fileCount === 1 ? 'memory' : 'memories'
      // Use toast instead of modal for upload success
      showToast(`${result.fileCount} ${photoWord} added`)

      // Silent refresh - no loading flash
      await fetchAlbums(true)

      // Refresh album files to show new uploads
      if (!isTempAlbum) {
        refreshAlbumFiles()
      }

      // Keep progress/previews visible longer for smooth transition
      // The checkmark shows briefly, then fades as real images load
      setTimeout(() => resetProgress(), 1500)
    } catch (error) {
      if (error instanceof UploadInProgressError) {
        // A run is already active - do NOT resetProgress() here, that would
        // wipe out the still-uploading run's visible progress/previews.
        showNotification('info', 'Upload In Progress', error.message)
        return
      }
      // Distinct from the generic failure below: each says why, and for a
      // read-only Memoir retrying cannot succeed, so a bare "something went
      // wrong" would send the photographer looking for a bug.
      const refusal = memoirUploadRefusal(error)
      if (refusal) {
        showNotification('error', refusal.title, refusal.message)
        resetProgress()
        return
      }
      folderLogger.error('Failed to upload photos', { error })
      showNotification('error', 'Something Went Wrong', uploadProgress.error || 'We couldn\'t add your photos. Please try again.')
      resetProgress()
    }
  }

  // Dropzone handlers for empty album
  const handleDropzoneDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDropzoneActive(true)
  }

  const handleDropzoneDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDropzoneActive(false)
  }

  const handleDropzoneDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDropzoneActive(false)

    if (!currentFolderId) return

    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      await uploadFilesToAlbum(currentFolderId, files)
    }
  }

  const handleDropzoneClick = () => {
    handleAddPhotosToCurrentAlbum()
  }

  return {
    fileInputRef,
    isDropzoneActive,
    uploadProgress,
    handleUploadToAlbum,
    handleAddPhotosToCurrentAlbum,
    handleFileInputChange,
    handleDropzoneDragOver,
    handleDropzoneDragLeave,
    handleDropzoneDrop,
    handleDropzoneClick,
  }
}
